import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, FileText, MessagesSquare, Network, ClipboardList,
  Folder, ShieldCheck, FileLock2, FileSpreadsheet, FileSignature,
  Building2, CircleDot, Check, Lock, MessageSquare, ChevronRight,
} from 'lucide-react';
import { useReveal } from '@/lib/useReveal';

/**
 * PlatformWorkspace — Phase 99A + Phase 102 (designed product visuals).
 *
 * Public section showing the GP Capital platform surface in a controlled,
 * NDA-safe way. Phase 102 replaces the original placeholder frames with
 * designed React/CSS/SVG visuals that truthfully represent the workflow
 * shape WITHOUT exposing any live deal, borrower, lender, document, amount,
 * email, or address. Every label below is a generic placeholder.
 *
 * Strict rules (Phase 102):
 *  - No real / masked / live screenshots.
 *  - No external image URLs, no stock photos, no fake lender logos.
 *  - No retail/yield language, no AI/predictive/recommended/smart wording.
 *  - No fabricated metrics, scores, amounts, dates, counterparties.
 *  - Each visual is captioned "Illustrative platform workflow" so the
 *    surface is unambiguously a designed visual, not a real capture.
 *  - Palette stays inside the Phase 99A public design tokens.
 */

const CARDS = [
  {
    id: 'pipeline',
    icon: ClipboardList,
    eyebrow: 'Deal pipeline',
    title: 'Mandates moving through one controlled workflow',
    body: 'Every opportunity sits in a single pipeline view — origination, review, NDA, distribution and execution stages tracked together, not across email threads and spreadsheets.',
  },
  {
    id: 'deal-room',
    icon: FileText,
    eyebrow: 'Deal room & document control',
    title: 'NDA-gated data room with kind-aware preview',
    body: 'Documents are uploaded, watermarked, version-controlled and previewed in-place. Permissions are enforced per counterparty so each lender sees only what their NDA covers.',
  },
  {
    id: 'matching',
    icon: Network,
    eyebrow: 'Mandate-fit matching',
    title: 'Suggested capital partners, by mandate not broadcast',
    body: 'Each transaction is matched to the institutional partners whose mandate fits the structure, sector, size band and jurisdiction — not blasted to a generic list.',
  },
  {
    id: 'qna',
    icon: MessagesSquare,
    eyebrow: 'Q&A governance & responses',
    title: 'Structured lender Q&A and response tracking',
    body: 'Lender questions are captured in one audit-aware thread per deal, with structured responses, attachments and indicative terms collected directly inside the platform.',
  },
];

/**
 * Shared frame — every Phase 102 visual sits inside this 16:10 surface so
 * the four cards stay structurally identical regardless of inner art.
 * Pure CSS, no images, no fabricated metrics.
 */
const VisualFrame = ({ children, ariaLabel }) => (
  <div
    role="img"
    aria-label={ariaLabel}
    style={{
      position: 'relative',
      aspectRatio: '16 / 10',
      borderRadius: '10px',
      background: 'var(--gpc-paper-2)',
      border: '1px solid var(--gpc-line)',
      overflow: 'hidden',
    }}
  >
    {/* subtle hairline grid — no data */}
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        backgroundImage:
          'linear-gradient(rgba(19,35,63,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(19,35,63,0.04) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        opacity: 0.55,
      }}
    />
    <div style={{ position: 'absolute', inset: 0, padding: '14px 14px 28px 14px', display: 'flex', flexDirection: 'column' }}>
      {children}
    </div>
    {/* corner caption — repeated on every visual so the surface reads
        unambiguously as an illustrative workflow, not a screenshot. */}
    <span
      style={{
        position: 'absolute',
        bottom: 8,
        right: 10,
        fontFamily: 'Space Grotesk, system-ui, sans-serif',
        fontSize: '9.5px',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--gpc-ink-soft)',
      }}
    >
      Illustrative platform workflow
    </span>
  </div>
);

/* ---------- mini primitives (shared across the 4 visuals) ---------- */

const Bar = ({ w = '60%', tone = 'soft' }) => (
  <span
    style={{
      display: 'inline-block',
      height: 6,
      width: w,
      borderRadius: 3,
      background:
        tone === 'strong'
          ? 'rgba(19,35,63,0.28)'
          : tone === 'gold'
          ? 'rgba(140,107,46,0.32)'
          : 'rgba(19,35,63,0.12)',
    }}
  />
);

const StatusPill = ({ tone = 'muted', children }) => {
  const map = {
    muted: { fg: 'var(--gpc-ink-soft)', bg: 'rgba(19,35,63,0.05)', bd: 'rgba(19,35,63,0.10)' },
    gold:  { fg: 'var(--gpc-ink-soft)',      bg: 'rgba(140,107,46,0.08)', bd: 'rgba(140,107,46,0.22)' },
    ok:    { fg: 'var(--gpc-ink-soft)',        bg: 'rgba(46,125,91,0.08)', bd: 'rgba(46,125,91,0.22)' },
    navy:  { fg: 'var(--gpc-navy)',      bg: 'rgba(15,40,71,0.06)',  bd: 'rgba(15,40,71,0.16)' },
  };
  const s = map[tone] || map.muted;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'Space Grotesk, system-ui, sans-serif',
        fontSize: 9,
        letterSpacing: '0.10em',
        textTransform: 'uppercase',
        color: s.fg,
        background: s.bg,
        border: `1px solid ${s.bd}`,
        padding: '2px 6px',
        borderRadius: 999,
        lineHeight: 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );
};

/* ---------- Visual 1 — Deal pipeline ---------- */

const PipelineVisual = () => {
  const stages = [
    { name: 'Intake',       count: 3, active: false },
    { name: 'Review',       count: 2, active: true  },
    { name: 'Distribution', count: 2, active: false },
    { name: 'Execution',    count: 1, active: false },
  ];
  return (
    <VisualFrame ariaLabel="Illustrative pipeline with four mandate stages: intake, review, distribution, execution">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <ClipboardList style={{ width: 12, height: 12, color: 'var(--gpc-gold)' }} />
        <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gpc-ink-soft)' }}>
          Mandate pipeline
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 5, flex: 1, minHeight: 0 }}>
        {stages.map((s) => (
          <div
            key={s.name}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 5,
              padding: '6px 6px',
              borderRadius: 5,
              background: s.active ? 'rgba(140,107,46,0.04)' : 'var(--gpc-paper)',
              border: `1px solid ${s.active ? 'rgba(140,107,46,0.22)' : 'var(--gpc-line)'}`,
              borderLeft: s.active ? '2px solid var(--gpc-gold)' : `1px solid var(--gpc-line)`,
              minWidth: 0,
              minHeight: 0,
              overflow: 'hidden',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 3, minWidth: 0 }}>
              <span
                style={{
                  fontFamily: 'Space Grotesk, system-ui, sans-serif',
                  fontSize: 7.5,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: s.active ? 'var(--gpc-gold)' : 'var(--gpc-ink-soft)',
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  flex: 1,
                }}
                title={s.name}
              >
                {s.name}
              </span>
              <span style={{ fontSize: 8, color: 'var(--gpc-ink-soft)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>0{s.count}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
              {Array.from({ length: s.count }).map((_, j) => {
                const isLead = s.active && j === 0;
                return (
                  <div
                    key={j}
                    aria-hidden="true"
                    style={{
                      borderRadius: 3,
                      background: 'var(--gpc-paper-2)',
                      border: `1px solid ${isLead ? 'rgba(140,107,46,0.30)' : 'var(--gpc-line)'}`,
                      padding: '4px 5px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 3,
                      boxShadow: isLead ? '0 1px 2px rgba(19,35,63,0.06)' : 'none',
                      minWidth: 0,
                    }}
                  >
                    <Bar w="80%" tone="strong" />
                    <Bar w="55%" tone={isLead ? 'gold' : 'soft'} />
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </VisualFrame>
  );
};

/* ---------- Visual 2 — Deal room ---------- */

const DealRoomVisual = () => {
  const folders = [
    { name: 'Financials',            icon: FileSpreadsheet, active: true,  count: 4 },
    { name: 'KYC',                   icon: ShieldCheck,     active: false, count: 3 },
    { name: 'Legal',                 icon: FileSignature,   active: false, count: 5 },
    { name: 'Valuation',             icon: Building2,       active: false, count: 2 },
    { name: 'Transaction documents', icon: FileLock2,       active: false, count: 3 },
  ];
  const previewDocs = [
    { tone: 'gold', label: 'Watermarked' },
    { tone: 'navy', label: 'Preview only' },
    { tone: 'ok',   label: 'Released' },
  ];
  return (
    <VisualFrame ariaLabel="Illustrative permissioned data room with folders for Financials, KYC, Legal, Valuation and Transaction documents">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Folder style={{ width: 12, height: 12, color: 'var(--gpc-gold)' }} />
        <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gpc-ink-soft)' }}>
          Permissioned data room
        </span>
        <span style={{ marginLeft: 'auto' }}><StatusPill tone="gold">NDA gated</StatusPill></span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '38% 1fr', gap: 7, flex: 1, minHeight: 0 }}>
        {/* folder rail */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, background: 'var(--gpc-paper)', border: '1px solid var(--gpc-line)', borderRadius: 5, padding: 5, minHeight: 0, overflow: 'hidden' }}>
          {folders.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 5px',
                  borderRadius: 3,
                  background: f.active ? 'var(--gpc-paper-2)' : 'transparent',
                  border: f.active ? '1px solid var(--gpc-line-strong)' : '1px solid transparent',
                  boxShadow: f.active ? '0 1px 1px rgba(19,35,63,0.05)' : 'none',
                  minWidth: 0,
                }}
              >
                <Icon style={{ width: 9, height: 9, color: f.active ? 'var(--gpc-gold)' : 'var(--gpc-ink-soft)', flexShrink: 0 }} />
                <span
                  style={{
                    fontSize: 8.5,
                    color: f.active ? 'var(--gpc-ink)' : 'var(--gpc-ink-soft)',
                    fontWeight: f.active ? 600 : 400,
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={f.name}
                >
                  {f.name}
                </span>
                <span style={{ fontSize: 8, color: 'var(--gpc-ink-soft)', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{f.count}</span>
              </div>
            );
          })}
        </div>
        {/* preview pane */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--gpc-paper-2)', border: '1px solid var(--gpc-line)', borderRadius: 5, padding: 6, minHeight: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <FileSpreadsheet style={{ width: 10, height: 10, color: 'var(--gpc-gold)' }} />
            <span style={{ fontSize: 9, color: 'var(--gpc-ink)', fontWeight: 600 }}>Financials</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
            {previewDocs.map((d, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '3px 5px',
                  borderRadius: 3,
                  background: 'var(--gpc-paper)',
                  border: '1px solid var(--gpc-line-soft)',
                  minWidth: 0,
                }}
              >
                <FileText style={{ width: 8, height: 8, color: 'var(--gpc-ink-soft)', flexShrink: 0 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                  <Bar w={`${78 - i * 8}%`} tone="strong" />
                  <Bar w={`${45 - i * 4}%`} tone="soft" />
                </div>
                <StatusPill tone={d.tone}>{d.label}</StatusPill>
              </div>
            ))}
          </div>
        </div>
      </div>
    </VisualFrame>
  );
};

/* ---------- Visual 3 — Mandate-fit matching ---------- */

const MatchingVisual = () => {
  // Three illustrative capital partner rows with non-numeric, dot-based fit indicators.
  const partners = [
    { label: 'Capital Partner A', status: 'Reviewing',  tone: 'gold',  fit: [true, true, true]  },
    { label: 'Capital Partner B', status: 'Indicating', tone: 'navy',  fit: [true, true, false] },
    { label: 'Capital Partner C', status: 'Passed',     tone: 'muted', fit: [true, false, true] },
  ];
  return (
    <VisualFrame ariaLabel="Illustrative mandate-fit matching showing three capital partners reviewing a structured mandate brief">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <Network style={{ width: 12, height: 12, color: 'var(--gpc-gold)' }} />
        <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gpc-ink-soft)' }}>
          Controlled distribution
        </span>
      </div>
      {/* mandate brief panel */}
      <div
        style={{
          background: 'var(--gpc-paper)',
          border: '1px solid var(--gpc-line)',
          borderRadius: 5,
          padding: '5px 8px',
          marginBottom: 6,
          display: 'flex',
          alignItems: 'center',
          gap: 7,
        }}
      >
        <div style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(140,107,46,0.08)', border: '1px solid rgba(140,107,46,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ClipboardList style={{ width: 9, height: 9, color: 'var(--gpc-gold)' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
          <span
            style={{
              fontSize: 8.5,
              color: 'var(--gpc-ink-soft)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              fontFamily: 'Space Grotesk, system-ui, sans-serif',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title="Mandate brief · sector · size band · jurisdiction · structure"
          >
            Mandate brief · sector · size band · jurisdiction
          </span>
          <div style={{ display: 'flex', gap: 4 }}>
            <Bar w="22%" tone="strong" />
            <Bar w="18%" tone="strong" />
            <Bar w="22%" tone="strong" />
            <Bar w="14%" tone="strong" />
          </div>
        </div>
      </div>
      {/* partner rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
        {partners.map((p) => (
          <div
            key={p.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              padding: '5px 7px',
              borderRadius: 5,
              background: 'var(--gpc-paper-2)',
              border: '1px solid var(--gpc-line)',
              minWidth: 0,
              flex: 1,
              minHeight: 0,
            }}
          >
            <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--gpc-paper)', border: '1px solid var(--gpc-line-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Building2 style={{ width: 8, height: 8, color: 'var(--gpc-ink-soft)' }} />
            </div>
            <span style={{ fontSize: 9.5, color: 'var(--gpc-ink)', fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</span>
            {/* fit dots — sector / size / jurisdiction; illustrative only, never numeric */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }} title="Sector · Size · Jurisdiction fit (illustrative)">
              {p.fit.map((on, i) => (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: on ? 'var(--gpc-gold)' : 'transparent',
                    border: on ? '1px solid rgba(140,107,46,0.30)' : '1px solid var(--gpc-line-strong)',
                  }}
                />
              ))}
            </div>
            <StatusPill tone={p.tone}>{p.status}</StatusPill>
          </div>
        ))}
      </div>
    </VisualFrame>
  );
};

/* ---------- Visual 4 — Q&A governance ---------- */

const QnaVisual = () => {
  const steps = [
    { label: 'Question received', tone: 'navy',  iconBg: 'var(--gpc-paper)',          dotColor: 'var(--gpc-ink-soft)', Icon: CircleDot, snippet: 'Lender · question on structure' },
    { label: 'Internal review',   tone: 'gold',  iconBg: 'rgba(140,107,46,0.08)',     dotColor: 'var(--gpc-gold)',     Icon: ShieldCheck, snippet: 'Coverage check · awaiting response' },
    { label: 'Approved response', tone: 'ok',    iconBg: 'rgba(46,125,91,0.10)',      dotColor: 'var(--gpc-ok)',       Icon: Check, snippet: 'Response signed off · ready to release' },
    { label: 'Released',          tone: 'muted', iconBg: 'rgba(19,35,63,0.05)',       dotColor: 'var(--gpc-ink-soft)', Icon: ArrowRight, snippet: 'Visible to permitted counterparty' },
  ];
  return (
    <VisualFrame ariaLabel="Illustrative Q&A governance workflow with four steps: question received, internal review, approved response, released">
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <MessageSquare style={{ width: 12, height: 12, color: 'var(--gpc-gold)' }} />
        <span style={{ fontFamily: 'Space Grotesk, system-ui, sans-serif', fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--gpc-ink-soft)' }}>
          Structured Q&amp;A
        </span>
        <span style={{ marginLeft: 'auto' }}><StatusPill tone="navy">Audit-aware</StatusPill></span>
      </div>
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
        {/* vertical track */}
        <div aria-hidden="true" style={{ position: 'absolute', left: 9, top: 6, bottom: 6, width: 1, background: 'var(--gpc-line)' }} />
        {steps.map((s, i) => {
          const Icon = s.Icon;
          return (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 7, position: 'relative', zIndex: 1, flex: 1, minHeight: 0 }}>
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: s.iconBg,
                  border: `1px solid ${i === 1 ? 'rgba(140,107,46,0.30)' : 'var(--gpc-line-strong)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon style={{ width: 9, height: 9, color: s.dotColor }} />
              </div>
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 7,
                  padding: '3px 7px',
                  borderRadius: 4,
                  background: 'var(--gpc-paper)',
                  border: '1px solid var(--gpc-line)',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      fontFamily: 'Space Grotesk, system-ui, sans-serif',
                      fontSize: 7.5,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'var(--gpc-ink-soft)',
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.label}
                  </span>
                  <span
                    style={{
                      fontSize: 8.5,
                      color: 'var(--gpc-ink-soft)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.snippet}
                  </span>
                </div>
                {i === 0 && <Lock style={{ width: 8, height: 8, color: 'var(--gpc-ink-soft)', flexShrink: 0 }} />}
                {i === 3 && <ChevronRight style={{ width: 9, height: 9, color: 'var(--gpc-ink-soft)', flexShrink: 0 }} />}
              </div>
            </div>
          );
        })}
      </div>
    </VisualFrame>
  );
};

/* ---------- Visual dispatcher ---------- */

const VISUALS = {
  pipeline:  PipelineVisual,
  'deal-room': DealRoomVisual,
  matching:  MatchingVisual,
  qna:       QnaVisual,
};

const VisualForCard = ({ id }) => {
  const Cmp = VISUALS[id];
  // Defensive: if dispatcher misses (should never happen), render an empty
  // captioned frame so the section still renders a neutral surface.
  if (!Cmp) {
    return (
      <VisualFrame ariaLabel="Platform workflow preview">
        <span style={{ margin: 'auto', fontSize: 10, color: 'var(--gpc-ink-soft)', letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Space Grotesk, system-ui, sans-serif' }}>
          Platform workflow preview
        </span>
      </VisualFrame>
    );
  }
  return <Cmp />;
};

export const PlatformWorkspace = () => {
  const [ref, visible] = useReveal({ threshold: 0.1 });

  return (
    <section
      ref={ref}
      className="gpc-section"
      data-testid="platform-workspace"
      style={{ background: 'var(--gpc-paper-2)' }}
    >
      <div className={`gpc-container gpc-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="max-w-3xl mb-12">
          <p className="gpc-eyebrow mb-4">Platform workspace</p>
          <h2 className="gpc-h2 mb-4">
            A controlled workspace for{' '}
            <span style={{ color: 'var(--gpc-gold)', fontStyle: 'italic' }}>private capital execution.</span>
          </h2>
          <p className="gpc-body max-w-2xl">
            From intake and document control to lender matching, structured responses and execution tracking, GP Capital keeps each mandate inside one permissioned workflow — not scattered across email threads and spreadsheets.
          </p>
        </div>

        <div
          className="grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
          }}
          data-testid="platform-workspace-grid"
        >
          {CARDS.map((card) => (
            <article
              key={card.id}
              className="gpc-card"
              data-testid={`workspace-card-${card.id}`}
              style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
            >
              <VisualForCard id={card.id} />
              <div>
                <p
                  style={{
                    fontFamily: 'Space Grotesk, system-ui, sans-serif',
                    fontSize: '11px',
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    color: 'var(--gpc-gold)',
                    marginBottom: '6px',
                  }}
                >
                  {card.eyebrow}
                </p>
                <h3 className="gpc-h3" style={{ marginBottom: '8px' }}>{card.title}</h3>
                <p
                  style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: '14px',
                    lineHeight: 1.6,
                    color: 'var(--gpc-ink-soft)',
                  }}
                >
                  {card.body}
                </p>
              </div>
            </article>
          ))}
        </div>

        <div
          className="flex flex-wrap items-center gap-3"
          style={{ marginTop: '32px' }}
          data-testid="platform-workspace-footer"
        >
          <Link to="/portal/login" className="gpc-cta-primary" data-testid="workspace-cta-enter">
            Enter platform
            <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="gpc-cta-secondary" data-testid="workspace-cta-request">
            Request a walkthrough
          </Link>
          <span
            style={{
              fontFamily: 'Space Grotesk, system-ui, sans-serif',
              fontSize: '11px',
              letterSpacing: '0.1em',
              color: 'var(--gpc-ink-soft)',
              marginLeft: 'auto',
            }}
          >
            Counterparty information shown only after NDA
          </span>
        </div>
      </div>
    </section>
  );
};

export default PlatformWorkspace;
